import { DraftsOutlined, ForwardToInbox, LocalActivityOutlined } from "@mui/icons-material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Box, Grid } from "@mui/material";
import { useState } from "react";
import GiftCarousel from "@/components/GiftCarousel";
import MetricCard from "@/components/MetricCard";
import { WelcomeOverlay } from "@/components/onboarding";
import QuickActions from "@/components/QuickActions";
import RecentCampaign from "@/components/RecentCampaign";
import SendGiftDrawer from "@/components/SendGiftDrawer";
import ThankYouNotes from "@/components/ThankYouNotes";
import UpcomingDates from "@/components/UpcomingDates";
import { useDashboardCampaigns } from "@/hooks/useDashboardCampaigns";
import { useDashboardDates } from "@/hooks/useDashboardDates";
import { useDashboardNotes } from "@/hooks/useDashboardNotes";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useDashboardTopGifts } from "@/hooks/useDashboardTopGifts";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { toUpcomingDates } from "@/utils/upcomingDates-helper";

// Based on client-dashboard.ejs
export default function Dashboard() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const { data: dashboardStats } = useDashboardStats();
  const { data: notes = [] } = useDashboardNotes();
  const { data: rawDates = [] } = useDashboardDates();
  const { data: rawCampaigns = [] } = useDashboardCampaigns();
  const { data: topGifts = [] } = useDashboardTopGifts();

  const metrics = dashboardStats
    ? [
        {
          title: "Invites",
          icon: <ForwardToInbox />,
          value: dashboardStats.invites_sent,
          subtitle: "This month",
        },
        {
          title: "Gratitude",
          icon: <DraftsOutlined />,
          value: dashboardStats.note_count,
          subtitle: "People sent their Thanks!",
        },
        {
          title: "Redemptions",
          icon: <LocalActivityOutlined />,
          value: `${dashboardStats.redemption_rate}%`,
          subtitle: dashboardStats.redemption_rate_capped
            ? `${dashboardStats.redeemed_count} redeemed exceeds ${dashboardStats.redemption_denominator} sent`
            : `${dashboardStats.redeemed_count} gifts redeemed so far`,
        },
        {
          title: "Charity Rate",
          icon: <FavoriteBorderIcon />,
          value: `${dashboardStats.charity_rate}%`,
          subtitle: `${dashboardStats.charity_impact_count} people chose a charity`,
        },
      ]
    : [];

  const campaigns = rawCampaigns.map((c) => ({
    ...c,
    startDate: new Date(c.startDate),
    endDate: new Date(c.endDate),
  }));

  // Annual observances rolled to their next occurrence (this year or next), nearest first, top 5 —
  // so nothing is ever in the past ("-202 Days") and the widget never empties as the year winds down.
  const upcomingDates = toUpcomingDates(rawDates);

  let isRising = false;
  try {
    isRising = !!sessionStorage.getItem("gfg_new_account");
  } catch {
    // silent
  }

  return (
    <Box className={isRising ? "gfg-rise" : ""}>
      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: isMobile ? 4 : 0 }}>
        {metrics.map((metric) => (
          <MetricCard metric={metric} key={metric.title} />
        ))}
      </Grid>

      {isMobile && <QuickActions setDrawerOpen={setDrawerOpen} />}

      {isMobile ? (
        // Mobile layout - stacked with carousel in between
        <>
          <SendGiftDrawer
            open={drawerOpen}
            onOpen={() => setDrawerOpen(true)}
            onClose={() => setDrawerOpen(false)}
          />

          {/* Carousel Collections */}
          <GiftCarousel gifts={topGifts} />

          {/* Dashboard Widgets */}
          <ThankYouNotes notes={notes} totalCount={dashboardStats?.note_count} />

          <UpcomingDates dates={upcomingDates} />

          <RecentCampaign campaigns={campaigns} totalCount={dashboardStats?.campaign_count} />
        </>
      ) : (
        <Grid container>
          {/* Dashboard Widgets */}
          <Grid container width="100%" spacing={3} sx={{ alignItems: "stretch" }}>
            <ThankYouNotes notes={notes.slice(0, 2)} totalCount={dashboardStats?.note_count} />

            <UpcomingDates dates={upcomingDates} />

            <RecentCampaign campaigns={campaigns} totalCount={dashboardStats?.campaign_count} />
          </Grid>

          {/* Carousel Collections */}
          <GiftCarousel gifts={topGifts} />
        </Grid>
      )}

      <WelcomeOverlay />
    </Box>
  );
}
